import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Document, Page } from 'react-pdf';
import styled from 'styled-components';
import { FileDoc, FilePlus, X } from '@phosphor-icons/react';
import { describeSegmentPages, isPdfRecord } from './builderUtils';

// Full-screen preview of the output document exactly as it will be
// assembled: every segment's pages, in output order, rendered client-side
// from the already-loaded sources (no server round-trip, nothing saved).
// Pages mount lazily as they scroll into view so a thousand-page packet
// doesn't render all at once. Office segments can't be rendered before
// conversion, so they show an explanatory placeholder.

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.55);
  z-index: 1600;
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const Panel = styled.div`
  background: #f7f9fc;
  border-radius: 10px;
  width: min(760px, 100%);
  /* Bound to the overlay so long packets scroll INSIDE PanelBody instead
     of growing the panel past the viewport (flex min-height:auto trap). */
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Manrope', sans-serif;
  color: #1b2028;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: #ffffff;
  border-bottom: 1px solid #dde2e9;
`;

const PanelTitle = styled.div`
  flex: 1;
  min-width: 0;

  h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    font-size: 12px;
    color: #8a97a8;
  }
`;

const HeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 6px;
  background: #0c53bb;
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    background: #aeb8c4;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: #5a6c87;
  display: inline-flex;

  &:hover {
    background: #f1f6fe;
  }
`;

const PanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px 32px;
`;

const SegmentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 18px 0 10px;
  font-size: 13px;
  color: #48566b;

  strong {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const LabelChip = styled.span`
  background: #eef5ff;
  border: 1px solid #c8dcf7;
  color: #0c53bb;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  white-space: nowrap;
`;

const PageFrame = styled.div`
  background: #ffffff;
  border: 1px solid #dde2e9;
  border-radius: 4px;
  margin: 0 auto 6px;
  width: 560px;
  max-width: 100%;
  min-height: ${(p) => (p.$placeholder ? '720px' : 'auto')};
  display: flex;
  align-items: center;
  justify-content: center;

  canvas {
    display: block;
    width: 100% !important;
    height: auto !important;
  }
`;

const PageCaption = styled.div`
  text-align: center;
  font-size: 11px;
  color: #8a97a8;
  margin-bottom: 14px;
`;

const OfficePlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px dashed #c9d2de;
  border-radius: 6px;
  margin: 0 auto 14px;
  width: 560px;
  max-width: 100%;
  padding: 40px 24px;
  font-size: 13px;
  color: #48566b;
  text-align: center;
`;

const SectionNote = styled.div`
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  margin: 0 auto 14px;
  width: 560px;
  max-width: 100%;
  padding: 14px;
  font-size: 12px;
  color: #9a3412;
`;

/**
 * Mounts its <Page> only while scrolled near the viewport and unmounts it
 * again once it leaves (plus a 600px lookahead band), so memory stays
 * bounded by the viewport on huge packets instead of accumulating every
 * visited canvas. The rendered height is remembered and reused as the
 * placeholder height so re-hiding a page doesn't make the scroll jump.
 * Falls back to rendering immediately where IntersectionObserver is
 * unavailable (jsdom).
 */
const LazyPage = ({ pageNumber, caption, scrollRoot }) => {
  const holderRef = useRef(null);
  const measuredHeightRef = useRef(null);
  const [visible, setVisible] = useState(
    typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !holderRef.current) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            // Remember the PAGE FRAME's height (first child), not the
            // whole holder — the caption stays mounted, so including it
            // would grow each hidden page by one caption and make
            // scrolling back through the preview jump.
            const frame = entry.target.firstElementChild;
            const height = frame?.getBoundingClientRect?.().height;
            if (height > 0) measuredHeightRef.current = height;
          }
          setVisible(entry.isIntersecting);
        }
      },
      // Root must be the overlay's own scroller — with the default
      // (viewport) root, the panel's overflow clipping zeroes
      // intersection outside the visible band and the 600px lookahead
      // never pre-mounts upcoming pages.
      { root: scrollRoot || null, rootMargin: '600px 0px' }
    );
    observer.observe(holderRef.current);
    return () => observer.disconnect();
  }, [scrollRoot]);

  const placeholderHeight = measuredHeightRef.current || 720;

  return (
    <div ref={holderRef}>
      <PageFrame
        $placeholder={!visible}
        style={!visible ? { minHeight: placeholderHeight } : undefined}
      >
        {visible ? (
          <Page
            pageNumber={pageNumber}
            width={560}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={<div style={{ height: placeholderHeight }} />}
          />
        ) : (
          <span style={{ color: '#c9d2de', fontSize: 12 }}>…</span>
        )}
      </PageFrame>
      <PageCaption>{caption}</PageCaption>
    </div>
  );
};

LazyPage.propTypes = {
  pageNumber: PropTypes.number.isRequired,
  caption: PropTypes.string.isRequired,
  scrollRoot: PropTypes.object,
};

const SegmentSection = ({
  segment,
  index,
  source,
  urlEntry,
  numPages,
  onNumPages,
  outputStart,
  scrollRoot,
}) => {
  const pdf = source ? isPdfRecord(source.record) : false;

  const file = useMemo(() => {
    if (!urlEntry?.url) return null;
    return urlEntry.withCredentials
      ? { url: urlEntry.url, withCredentials: true }
      : { url: urlEntry.url };
  }, [urlEntry]);

  const pages = useMemo(() => {
    if (segment.pages) return segment.pages;
    if (numPages) {
      return Array.from({ length: numPages }, (_, i) => i + 1);
    }
    return [];
  }, [segment.pages, numPages]);

  const captionFor = (position, sourcePage) => {
    const outputNumber =
      outputStart != null ? `p. ${outputStart + position}` : `p. ≈?`;
    return `${outputNumber} — ${segment.sourceName}, page ${sourcePage}`;
  };

  return (
    <section data-testid={`preview-segment-${index}`}>
      <SegmentHeader>
        <strong>
          {index + 1}. {segment.sourceName}
        </strong>
        <span>{describeSegmentPages(segment)}</span>
        {segment.label && segment.label.trim() && (
          <LabelChip>Stamped: {segment.label.trim()}</LabelChip>
        )}
      </SegmentHeader>

      {!pdf && (
        <OfficePlaceholder data-testid={`preview-office-${index}`}>
          <FileDoc size={28} color="#0c53bb" />
          {segment.sourceName} is an Office document — it is converted to
          PDF during assembly, so its pages can’t be previewed here. It will
          appear in full at this position.
        </OfficePlaceholder>
      )}

      {pdf && urlEntry?.error && (
        <SectionNote>
          This document couldn’t be loaded for preview. It will still be
          included when the PDF is created.
        </SectionNote>
      )}

      {pdf && !urlEntry?.error && !file && (
        <SectionNote as="div" style={{ color: '#48566b', background: '#fff', borderColor: '#dde2e9' }}>
          Loading document…
        </SectionNote>
      )}

      {pdf && file && (
        <Document
          file={file}
          onLoadSuccess={({ numPages: total }) => onNumPages(source.key, total)}
          loading={
            <SectionNote as="div" style={{ color: '#48566b', background: '#fff', borderColor: '#dde2e9' }}>
              Loading document…
            </SectionNote>
          }
          error={
            <SectionNote>
              This document couldn’t be rendered for preview. It will still
              be included when the PDF is created.
            </SectionNote>
          }
        >
          {pages.map((sourcePage, position) => (
            <LazyPage
              key={`${segment.id}-${sourcePage}-${position}`}
              pageNumber={sourcePage}
              caption={captionFor(position, sourcePage)}
              scrollRoot={scrollRoot}
            />
          ))}
        </Document>
      )}
    </section>
  );
};

SegmentSection.propTypes = {
  segment: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  source: PropTypes.object,
  urlEntry: PropTypes.object,
  numPages: PropTypes.number,
  onNumPages: PropTypes.func.isRequired,
  outputStart: PropTypes.number,
  scrollRoot: PropTypes.object,
};

const OutputPreview = ({
  segments,
  sourcesByKey,
  sourceUrls,
  numPagesBySource,
  onNumPages,
  outputName,
  onClose,
  onCreate,
  isSubmitting,
}) => {
  // The overlay's own scroller — LazyPage observers need it as their
  // IntersectionObserver root or the lookahead band never triggers.
  const [scrollRoot, setScrollRoot] = useState(null);
  // Running output page number per segment; becomes unknown (null) after
  // any segment whose length we can't know yet (unloaded PDF, Office doc).
  const outputStarts = useMemo(() => {
    const starts = [];
    let next = 1;
    for (const segment of segments) {
      starts.push(next);
      if (next == null) continue;
      if (segment.pages) {
        next += segment.pages.length;
      } else {
        const source = sourcesByKey[segment.sourceKey];
        const count =
          source && isPdfRecord(source.record)
            ? numPagesBySource[segment.sourceKey]
            : null;
        next = count ? next + count : null;
      }
    }
    return starts;
  }, [segments, sourcesByKey, numPagesBySource]);

  const knownTotal = useMemo(() => {
    let total = 0;
    let allKnown = true;
    for (const segment of segments) {
      if (segment.pages) {
        total += segment.pages.length;
      } else {
        const count = numPagesBySource[segment.sourceKey];
        if (count) total += count;
        else allKnown = false;
      }
    }
    return { total, allKnown };
  }, [segments, numPagesBySource]);

  return (
    <Overlay data-testid="output-preview" onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <PanelHeader>
          <PanelTitle>
            <h2>{outputName || 'Output preview'}</h2>
            <span>
              {segments.length} item{segments.length === 1 ? '' : 's'}
              {knownTotal.total > 0 &&
                ` · ${knownTotal.allKnown ? '' : '≥ '}${knownTotal.total} page${
                  knownTotal.total === 1 ? '' : 's'
                }`}
              {' '}· exhibit labels & bates are stamped server-side
            </span>
          </PanelTitle>
          <HeaderButton
            type="button"
            data-testid="preview-create-button"
            disabled={segments.length === 0 || isSubmitting}
            onClick={onCreate}
          >
            <FilePlus size={16} />
            Create PDF
          </HeaderButton>
          <CloseButton
            type="button"
            aria-label="Close preview"
            data-testid="preview-close"
            onClick={onClose}
          >
            <X size={18} />
          </CloseButton>
        </PanelHeader>
        <PanelBody ref={setScrollRoot}>
          {segments.map((segment, index) => (
            <SegmentSection
              key={segment.id}
              segment={segment}
              index={index}
              source={sourcesByKey[segment.sourceKey]}
              urlEntry={sourceUrls[segment.sourceKey]}
              numPages={numPagesBySource[segment.sourceKey]}
              onNumPages={onNumPages}
              outputStart={outputStarts[index]}
              scrollRoot={scrollRoot}
            />
          ))}
        </PanelBody>
      </Panel>
    </Overlay>
  );
};

OutputPreview.propTypes = {
  segments: PropTypes.array.isRequired,
  sourcesByKey: PropTypes.object.isRequired,
  sourceUrls: PropTypes.object.isRequired,
  numPagesBySource: PropTypes.object.isRequired,
  onNumPages: PropTypes.func.isRequired,
  outputName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

export default OutputPreview;
